import ActiveSearchFilters from './ActiveSearchFilters';

const PlusIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const SpinnerIcon = ({ className = 'h-4 w-4' }) => (
  <svg
    className={`${className} animate-spin`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * Reusable list-page section: title, description, Active+Search filters with optional primary action, then content.
 * Use for list pages (cost centers, currencies, partners, etc.) to keep layout consistent.
 *
 * primaryAction: { label, onClick, disabled } or { element } – renders a primary action in the filter row.
 * secondaryAction: optional { label, onClick, disabled, loading?, loadingLabel? } – outline button placed before the primary action.
 *   When loading is true, shows a spinner and loadingLabel (fallback: label) and should be disabled.
 * compact: smaller typography and controls to align with dense tables (e.g. My Diary activity plan).
 * toolbar: optional node that replaces the default ActiveSearchFilters block (custom filter + search layout).
 */
const ListPageSection = ({
  title,
  description,
  headerActions,
  activeState,
  onActiveChange,
  search,
  onSearchChange,
  showActiveFilter = true,
  showSearchFilter = true,
  searchPlaceholder = 'Search…',
  onFilterChange,
  activeSelectId = 'filter-active',
  searchInputId = 'filter-search',
  leadingFilters,
  secondaryAction,
  primaryAction,
  compact = false,
  /** When set, replaces ActiveSearchFilters + primary action (search/leadingFilters props ignored). */
  toolbar,
  children,
}) => {
  const renderSecondaryAction = () => {
    if (!secondaryAction?.label) return null;
    const loading = !!secondaryAction.loading;
    const text = loading && secondaryAction.loadingLabel ? secondaryAction.loadingLabel : secondaryAction.label;
    const base =
      'inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-primary-dark/25 bg-white px-4 py-2 text-sm font-medium text-primary-dark shadow-sm transition hover:bg-cream/40 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';
    const small =
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary-dark/25 bg-white px-2.5 py-1.5 text-[10px] font-semibold text-primary-dark shadow-sm transition hover:bg-cream/40 focus:outline-none focus:ring-2 focus:ring-primary/35 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60';
    return (
      <button
        type="button"
        onClick={secondaryAction.onClick}
        className={compact ? small : base}
        disabled={secondaryAction.disabled}
        aria-busy={loading}
      >
        {loading ? (
          <>
            <SpinnerIcon className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
            <span>{text}</span>
          </>
        ) : (
          secondaryAction.label
        )}
      </button>
    );
  };

  const renderPrimaryAction = () => {
    if (primaryAction?.element) return primaryAction.element;
    if (!primaryAction?.label) return null;
    if (compact) {
      return (
        <button
          type="button"
          onClick={primaryAction.onClick}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-2.5 py-1.5 text-[10px] font-semibold text-text-inverse shadow-sm transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/35 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={primaryAction.disabled}
        >
          <PlusIcon className="h-3 w-3" />
          {primaryAction.label}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={primaryAction.onClick}
        className={
          // Match new theme tokens (see `shared/Button.jsx`), but keep this control inline-sized.
          'inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-primary px-4 py-2 ' +
          'text-sm text-text-inverse shadow-md transition ' +
          'hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ' +
          'disabled:cursor-not-allowed disabled:opacity-60'
        }
        disabled={primaryAction.disabled}
      >
        <PlusIcon />
        {primaryAction.label}
      </button>
    );
  };

  return (
    <div className="rounded-2xl border border-cream/20 bg-white shadow-sm">
      <div
        className={
          compact
            ? 'px-4 py-4 text-[11px] leading-snug text-slate-600 sm:px-5 sm:py-5'
            : 'px-6 py-6 text-sm text-slate-600'
        }
      >
        <div className="flex items-start justify-between gap-4">
          <p className={compact ? 'text-sm font-bold text-primary-dark' : 'text-base font-bold text-primary-dark'}>
            {title}
          </p>
          {headerActions ? <div className="shrink-0">{headerActions}</div> : null}
        </div>
        {description && (
          <p
            className={
              compact ? 'mt-1.5 text-[10px] leading-relaxed text-slate-500' : 'mt-2 text-sm text-slate-500'
            }
          >
            {description}
          </p>
        )}
        <div className={compact ? 'mt-3 border-t border-primary-dark/20' : 'mt-4 border-t border-primary-dark/20'} aria-hidden />

        <div className={compact ? 'mt-4 space-y-4' : 'mt-6 space-y-6'}>
          {toolbar != null ? (
            toolbar
          ) : (
            <ActiveSearchFilters
              activeState={activeState}
              onActiveChange={onActiveChange}
              search={search}
              onSearchChange={onSearchChange}
              showActiveFilter={showActiveFilter}
              showSearchFilter={showSearchFilter}
              searchPlaceholder={searchPlaceholder}
              activeSelectId={activeSelectId}
              searchInputId={searchInputId}
              onFilterChange={onFilterChange}
              leadingFilters={leadingFilters}
              compact={compact}
            >
              <div className={`flex flex-wrap items-end ${compact ? 'gap-2' : 'gap-2'}`}>
                {renderSecondaryAction()}
                {renderPrimaryAction()}
              </div>
            </ActiveSearchFilters>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default ListPageSection;
